import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import StructuredData from '@/components/base/StructuredData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, type Category } from '@/mocks/categories';

const tabs = [
  { key: 'all', label: '全部' },
  ...categories.map((c) => ({ key: c.id, label: c.name })),
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [animateKey, setAnimateKey] = useState(0);

  useEffect(() => {
    if (activeTab === 'all') {
      setSelectedCategory(null);
    } else {
      const found = categories.find((c) => c.id === activeTab) || null;
      setSelectedCategory(found);
    }
    setAnimateKey((k) => k + 1);
  }, [activeTab]);

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const displayCategories = selectedCategory ? [selectedCategory] : categories;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '游戏分类' },
  ];

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '游戏分类 - 澳门新葡京娱乐平台',
    description: '探索澳门新葡京多元游戏世界，超过800款精品游戏涵盖老虎机、桌面游戏、真人视讯、体育竞技等六大品类。',
    url: 'https://www.xinpujing.mo/category',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.xinpujing.mo/' },
      { '@type': 'ListItem', position: 2, name: '游戏分类' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <StructuredData data={webPageSchema} />
      <StructuredData data={breadcrumbSchema} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <div className="px-4 md:px-6 pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                游戏分类
              </h1>
              <p className="text-sm text-foreground-500">
                探索多元游戏世界，找到最适合您的娱乐体验
              </p>
            </div>
          </div>
        </div>

        <div className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full py-12 md:py-16 bg-background-50" key={animateKey}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {selectedCategory && (
              <div className="mb-10 p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 shrink-0">
                    <i className={`${selectedCategory.icon} text-2xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">
                      {selectedCategory.name}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {selectedCategory.description}
                    </p>
                  </div>
                  <div className="text-center shrink-0">
                    <div className="text-3xl font-heading font-bold text-primary-600">{selectedCategory.gameCount}</div>
                    <div className="text-xs text-foreground-400">款精品游戏</div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {displayCategories.map((category, index) => (
                <div
                  key={category.id}
                  className="group p-6 md:p-7 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.08}s` }}
                  onClick={() => handleTabChange(category.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-300">
                      <i className={`${category.icon} text-xl`}></i>
                    </div>
                    <span className="text-xs text-foreground-400 font-medium px-2 py-1 rounded-md bg-background-200/70 whitespace-nowrap">
                      {category.gameCount}款游戏
                    </span>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                    {category.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {category.description}
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-sm text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    查看详情
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}